import { z } from 'zod';
import { capacityCategoryValues } from 'src/apis/monolith/mappings';
import {
  bulletinBoardNoticesSortByValues,
  proposalTypeValues,
  statusValues,
  transactionTypeValues,
} from 'src/apis/secondary-market-service/mappings';
import { networkPointSuggestionSchema } from 'src/components/domain-specifics/network-points/monolith-network-point-multi-select';
import {
  descSortDirectionSchema,
  getPaginationSchema,
} from 'src/components/form/zod-schemas';
import {
  optionalDefault,
  preprocessAsArray,
} from 'src/components/form/zod-utilities';
import { usePageParamsFromSchema } from 'src/hooks/use-page-params-from-schema';

const schema = z.object({
  sortBy: z.enum(bulletinBoardNoticesSortByValues).default('CREATED_AT'),
  sortDirection: descSortDirectionSchema,
  proposalType: z.preprocess(
    preprocessAsArray,
    z.array(z.enum(proposalTypeValues)).default([])
  ),
  transactionType: z.preprocess(
    preprocessAsArray,
    z.array(z.enum(transactionTypeValues)).default([])
  ),
  networkPointId: z.preprocess(
    preprocessAsArray,
    z.array(networkPointSuggestionSchema).default([])
  ),
  capacityCategory: z.preprocess(
    preprocessAsArray,
    z.array(z.enum(capacityCategoryValues)).default([])
  ),
  runtimeFrom: z.string().nullable().default(null),
  runtimeTo: z.string().nullable().default(null),
  amountMin: z.coerce
    .number()
    .int('{label} cannot contain decimals.')
    .min(0, '{label} must be greater than or equal to 0.')
    .nullable()
    .default(null),
  amountMax: z.coerce
    .number()
    .int('{label} cannot contain decimals.')
    .min(0, '{label} must be greater than or equal to 0.')
    .nullable()
    .default(null),
  priceMin: z.coerce.number().nullable().default(null),
  priceMax: z.coerce.number().nullable().default(null),
  status: z.preprocess(
    optionalDefault(['ACTIVE']),
    z.preprocess(preprocessAsArray, z.array(z.enum(statusValues)))
  ),
  ...getPaginationSchema().shape,
});

export const useBulletinBoardPageParams = () => {
  return usePageParamsFromSchema(schema, {
    groupPrefix: 'bulletinBoard',
    filters: {
      proposalType: { label: 'Proposal Type' },
      transactionType: { label: 'Transaction Type' },
      networkPointId: { label: 'Network Point' },
      capacityCategory: { label: 'Capacity Category' },
      runtimeFrom: { label: 'Runtime' },
      runtimeTo: { label: 'Runtime' },
      amountMin: { label: 'Amount' },
      amountMax: { label: 'Amount' },
      priceMin: { label: 'Price' },
      priceMax: { label: 'Price' },
      status: { label: 'Status' },
    },
  });
};

export type BulletinBoardPageParams = ReturnType<
  typeof useBulletinBoardPageParams
>;
